import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, Menu, X, User, LogOut, ChevronDown, ArrowRight, Coffee } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useCartStore } from '@/data/cart';
import { useAuth } from '@/hooks/use-auth';
import { useCategories } from '@/hooks/use-products';
import { useShopPause } from '@/hooks/use-shop-pause';
import ThemeToggleButton from '@/components/ThemeToggleButton';

const navLinks = [
  { to: '/', label: 'Startseite' },
  { to: '/shop', label: 'Shop', hasDropdown: true },
  { to: '/ueber-uns', label: 'Über uns' },
  { to: '/galerie', label: 'Galerie' },
  { to: '/#kontakt', label: 'Kontakt' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [shopDropdownOpen, setShopDropdownOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout>>();
  const navigate = useNavigate();
  const location = useLocation();
  const totalItems = useCartStore((s) => s.totalItems());
  const { user, signOut } = useAuth();
  const { data: categories = [] } = useCategories();
  const { data: shopPause } = useShopPause();
  const shopClosed = !!shopPause?.paused;

  const categoryOrder = ['damen', 'herren', 'personalisierte-blaetter', 'geschenkideen', 'dekoration'];
  const parentCategories = categories
    .filter(c => !c.parent_id)
    .sort((a, b) => {
      const ai = categoryOrder.indexOf(a.slug);
      const bi = categoryOrder.indexOf(b.slug);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  const getSubcategories = (parentId: string) =>
    categories.filter(c => c.parent_id === parentId).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const openDropdown = () => {
    clearTimeout(dropdownTimeout.current);
    setShopDropdownOpen(true);
  };
  const closeDropdown = () => {
    dropdownTimeout.current = setTimeout(() => setShopDropdownOpen(false), 150);
  };

  // Lock body scroll when mobile menu is open + close on ESC + close on route change
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setMobileShopOpen(false);
  }, [location.pathname, location.search, location.hash]);

  const handleNavClick = (to: string) => {
    setOpen(false);
    if (to.includes('#')) {
      const [path, hash] = to.split('#');
      if (location.pathname === (path || '/')) {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(to);
      }
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container flex h-14 md:h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-1.5 md:gap-2">
          <span className="font-heading text-xl md:text-2xl font-bold text-primary">cycy</span>
          <span className="text-[10px] md:text-xs font-body text-muted-foreground tracking-widest uppercase">art</span>
        </Link>

        <nav className="hidden md:flex items-center gap-5 lg:gap-8">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div
                key={link.to}
                className="relative"
                onMouseEnter={openDropdown}
                onMouseLeave={closeDropdown}
              >
                <Link
                  to={link.to}
                  className={`text-sm font-medium transition-colors hover:text-primary inline-flex items-center gap-1 ${
                    location.pathname.startsWith('/shop') ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${shopDropdownOpen ? 'rotate-180' : ''}`} />
                </Link>

                {shopDropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50">
                    <div className="bg-popover text-popover-foreground border border-border/30 rounded-2xl shadow-2xl shadow-black/10 min-w-[320px] py-3 px-2 animate-fade-in">
                      {/* Alle Produkte */}
                      <Link
                        to="/shop"
                        onClick={() => setShopDropdownOpen(false)}
                        className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold text-foreground hover:bg-accent/60 transition-colors group"
                      >
                        <span>Alle Produkte</span>
                        <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-70 transition-opacity text-primary" />
                      </Link>

                      <div className="h-px bg-border/25 mx-3 my-2" />

                      {/* Categories with images */}
                      <div className="space-y-0.5">
                        {parentCategories.map((cat) => {
                          const subs = getSubcategories(cat.id);
                          return (
                            <div key={cat.id}>
                              <Link
                                to={`/shop?category=${cat.slug}`}
                                onClick={() => setShopDropdownOpen(false)}
                                className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-accent/50 transition-all group"
                              >
                                {cat.image && (
                                  <img
                                    src={cat.image}
                                    alt={`Kategorie ${cat.name} bei cycy art`}
                                    className="h-9 w-9 rounded-lg object-cover ring-1 ring-border/20 group-hover:ring-primary/30 transition-all"
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium text-foreground/90 group-hover:text-foreground transition-colors">
                                    {cat.name}
                                  </span>
                                </div>
                                <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-70 transition-opacity text-primary" />
                              </Link>
                              {subs.length > 0 && (
                                <div className="ml-[3.25rem] pl-3 border-l border-primary/10 pb-1 mb-0.5">
                                  {subs.map((sub) => (
                                    <Link
                                      key={sub.id}
                                      to={`/shop?category=${sub.slug}`}
                                      onClick={() => setShopDropdownOpen(false)}
                                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-colors"
                                    >
                                      {sub.image ? (
                                        <img src={sub.image} alt={`Unterkategorie ${sub.name}`} className="h-5 w-5 rounded object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                                      ) : (
                                        <span className="h-1.5 w-1.5 rounded-full bg-primary/30" />
                                      )}
                                      {sub.name}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                onClick={(e) => {
                  if (link.to.includes('#')) {
                    e.preventDefault();
                    handleNavClick(link.to);
                  }
                }}
                className={`relative text-sm font-medium transition-colors hover:text-primary py-1 after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-primary after:transition-transform after:origin-center ${
                  location.pathname === link.to
                    ? 'text-primary after:scale-x-100'
                    : 'text-muted-foreground after:scale-x-0 hover:after:scale-x-100'
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-4">
          {user && (
            <div className="hidden md:flex items-center gap-2">
              {user.user_metadata?.avatar_url ? (
                <img src={user.user_metadata.avatar_url} alt="" className="h-7 w-7 rounded-full" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
              ) : (
                <User className="h-5 w-5 text-muted-foreground" />
              )}
              <span className="text-sm text-muted-foreground">{user.user_metadata?.full_name || user.email}</span>
              <button onClick={signOut} className="p-2 hover:text-primary transition-colors" title="Abmelden">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}

          <ThemeToggleButton />

          <Link to="/warenkorb" className="relative inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-accent/60 hover:text-primary transition-colors" aria-label="Warenkorb">
            <ShoppingBag className="h-5 w-5" />
            {totalItems > 0 && (
              <span className="absolute top-1 right-1 h-[18px] min-w-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[11px] leading-none flex items-center justify-center font-bold ring-2 ring-background">
                {totalItems}
              </span>
            )}
          </Link>

          <button
            className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-accent/60 transition-colors"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Temporary closure notice */}
      {shopClosed && (
        <div
          className="border-b border-border/60"
          style={{ background: 'hsl(var(--gold) / 0.1)' }}
          role="status"
        >
          <div className="container flex items-center justify-center gap-2 py-1.5 text-center text-[11px] md:text-xs leading-tight">
            <Coffee className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--gold))]" aria-hidden="true" />
            <span className="font-medium text-foreground">Vorübergehend geschlossen</span>
            <span className="hidden sm:inline text-muted-foreground">
              – Bestellungen sind aktuell nicht möglich.
            </span>
          </div>
        </div>
      )}

      {/* Mobile menu overlay backdrop */}
      {open && (
        <button
          aria-label="Menü schließen"
          onClick={() => setOpen(false)}
          className="md:hidden fixed inset-0 top-14 z-40 bg-background/60 backdrop-blur-sm animate-fade-in"
        />
      )}

      {open && (
        <nav id="mobile-nav" aria-label="Hauptmenü" className="md:hidden fixed left-0 right-0 top-14 z-50 max-h-[calc(100vh-3.5rem)] overflow-y-auto border-t border-border bg-background p-4 shadow-xl animate-fade-in">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div key={link.to}>
                <button
                  onClick={() => setMobileShopOpen(!mobileShopOpen)}
                  className={`flex items-center justify-between w-full min-h-11 py-3 text-base font-medium transition-colors hover:text-primary ${
                    location.pathname.startsWith('/shop') ? 'text-primary' : 'text-foreground'
                  }`}
                >
                  {link.label}
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileShopOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileShopOpen && (
                   <div className="pl-2 pb-2 space-y-0.5">
                    <Link
                      to="/shop"
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-between py-2 px-2 rounded-lg text-sm font-semibold text-foreground hover:bg-accent/60 transition-colors"
                    >
                      <span>Alle Produkte</span>
                      <ArrowRight className="h-3.5 w-3.5 text-primary opacity-50" />
                    </Link>
                    <div className="h-px bg-border/25 mx-2 my-1" />
                    {parentCategories.map((cat) => {
                      const subs = getSubcategories(cat.id);
                      return (
                        <div key={cat.id}>
                          <Link
                            to={`/shop?category=${cat.slug}`}
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-2.5 py-2 px-2 rounded-lg hover:bg-accent/50 transition-colors group"
                          >
                            {cat.image && (
                              <img src={cat.image} alt={`Kategorie ${cat.name} bei cycy art`} className="h-8 w-8 rounded-lg object-cover ring-1 ring-border/20" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                            )}
                            <span className="text-sm font-medium text-foreground/90">{cat.name}</span>
                          </Link>
                          {subs.length > 0 && (
                            <div className="ml-[2.75rem] pl-2.5 border-l border-primary/10 pb-0.5 mb-0.5">
                              {subs.map((sub) => (
                                <Link
                                  key={sub.id}
                                  to={`/shop?category=${sub.slug}`}
                                  onClick={() => setOpen(false)}
                                  className="flex items-center gap-2 py-1.5 px-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-colors"
                                >
                                  {sub.image ? (
                                    <img src={sub.image} alt={`Unterkategorie ${sub.name}`} className="h-5 w-5 rounded object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                                  ) : (
                                    <span className="h-1.5 w-1.5 rounded-full bg-primary/30" />
                                  )}
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                onClick={(e) => {
                  if (link.to.includes('#')) {
                    e.preventDefault();
                    handleNavClick(link.to);
                  } else {
                    setOpen(false);
                  }
                }}
                className={`block min-h-11 py-3 text-base font-medium transition-colors hover:text-primary ${
                  location.pathname === link.to ? 'text-primary' : 'text-foreground'
                }`}
              >
                {link.label}
              </Link>
            )
          )}
          {user && (
            <div className="border-t border-border mt-2 pt-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {user.user_metadata?.avatar_url ? (
                    <img src={user.user_metadata.avatar_url} alt="" className="h-7 w-7 rounded-full" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                  ) : (
                    <User className="h-5 w-5 text-muted-foreground" />
                  )}
                  <span className="text-sm text-muted-foreground truncate max-w-[200px]">{user.user_metadata?.full_name || user.email}</span>
                </div>
                <button onClick={() => { signOut(); setOpen(false); }} className="p-2 hover:text-primary transition-colors" title="Abmelden">
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </nav>
      )}
    </header>
  );
}
